import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Moon, Sun, Stethoscope, Shield, Truck, Plane, PlaneTakeoff, Train, Coffee, Sparkles, Clock, Bell } from "lucide-react";
import AuthModal from "@/components/AuthModal";
import ReviewsSection from "@/components/ReviewsSection";
import ReviewsMarquee from "@/components/ReviewsMarquee";

const personas = [
  { icon: Stethoscope, key: "doctor", label: "Doctors & Nurses", color: "text-indigo-300" },
  { icon: PlaneTakeoff, key: "pilot", label: "Pilots", color: "text-sky-300" },
  { icon: Coffee, key: "steward", label: "Cabin Crew", color: "text-rose-300" },
  { icon: Train, key: "train_driver", label: "Train Drivers", color: "text-violet-300" },
  { icon: Shield, key: "security", label: "Security", color: "text-emerald-300" },
  { icon: Truck, key: "courier", label: "Couriers", color: "text-amber-300" },
  { icon: Plane, key: "traveler", label: "Frequent Travelers", color: "text-cyan-300" },
];

export default function Landing() {
  const [authOpen, setAuthOpen] = useState(false);
  const [authMode, setAuthMode] = useState("login");
  const { error } = useAuth();

  return (
    <div className="min-h-screen bg-[#07090E] text-slate-100 grain relative overflow-hidden">
      {/* Ambient gradient */}
      <div className="absolute -top-40 -right-40 w-[600px] h-[600px] rounded-full bg-indigo-500/10 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-40 -left-40 w-[500px] h-[500px] rounded-full bg-amber-500/5 blur-3xl pointer-events-none" />

      <nav className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 py-6 flex items-center justify-between">        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-indigo-500 to-indigo-700 flex items-center justify-center glow-indigo">
            <Moon className="w-5 h-5 text-white" />
          </div>
          <span className="font-display text-xl font-bold tracking-tight">CircaShift</span>
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            className="text-slate-300 hover:text-white hover:bg-white/5 rounded-full"
            data-testid="nav-login-btn"
            onClick={() => { setAuthMode("login"); setAuthOpen(true); }}
          >
            Sign in
          </Button>
          <Button
            className="rounded-full bg-indigo-500 hover:bg-indigo-400 text-white glow-indigo"
            data-testid="nav-signup-btn"
            onClick={() => { setAuthMode("register"); setAuthOpen(true); }}
          >
            Get Started
          </Button>
        </div>
      </nav>

      <ReviewsMarquee />

      <main className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 pt-10 pb-24">
        <div className="grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <div className="pill text-indigo-300 border-indigo-500/30 bg-indigo-950/50 mb-6">
              <Sparkles className="w-3.5 h-3.5" /> Adaptive · Circadian · AI-guided
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05] mb-6">
              Sleep that <span className="text-indigo-400">bends</span><br />
              around your shifts —<br />
              not the other way.
            </h1>
            <p className="text-base sm:text-lg text-slate-300 max-w-xl leading-relaxed mb-8">
              For doctors, nurses, pilots, cabin crew, train drivers, security teams,
              couriers, and frequent travelers. CircaShift adapts anchor sleep,
              caffeine cutoffs, and light exposure in real time as your schedule shifts.
            </p>
            <div className="flex flex-wrap gap-3">
              <Button
                size="lg"
                data-testid="hero-cta-signup"
                onClick={() => { setAuthMode("register"); setAuthOpen(true); }}
                className="rounded-full bg-indigo-500 hover:bg-indigo-400 text-white px-8 h-12 text-base glow-indigo"
              >
                Start free
              </Button>
              <Button
                size="lg"
                variant="outline"
                data-testid="hero-cta-signin"
                onClick={() => { setAuthMode("login"); setAuthOpen(true); }}
                className="rounded-full bg-transparent border-white/15 text-slate-200 hover:bg-white/5 px-8 h-12 text-base"
              >
                I already have an account
              </Button>
            </div>

            <div className="mt-10 flex flex-wrap gap-3">
              {personas.map((p) => (
                <div key={p.key} className="pill bg-slate-900/60 border-white/10">
                  <p.icon className={`w-3.5 h-3.5 ${p.color}`} /> {p.label}
                </div>
              ))}
            </div>
          </div>

          {/* Right panel — timeline preview */}
          <div className="lg:col-span-5">
            <div className="circa-card p-6 relative overflow-hidden">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <div className="pill text-amber-300 border-amber-500/30 bg-amber-950/40 mb-2">
                    <Clock className="w-3.5 h-3.5" /> Live 24h Timeline
                  </div>
                  <div className="font-display text-lg font-semibold">Adaptive schedule</div>
                </div>
                <Bell className="w-5 h-5 text-slate-500" />
              </div>
              <div className="space-y-3">
                <TimelinePreviewRow label="Anchor Sleep" time="22:00 – 05:30" color="bg-indigo-500/70" pct={70} />
                <TimelinePreviewRow label="Wind Down" time="21:00 – 22:00" color="bg-violet-500/70" pct={40} />
                <TimelinePreviewRow label="Caffeine Cutoff" time="14:00 – 22:00" color="bg-rose-500/60" pct={55} />
                <TimelinePreviewRow label="Bright Light" time="06:00 – 06:30" color="bg-amber-500/70" pct={20} />
                <TimelinePreviewRow label="Night Shift" time="07:00 – 19:00" color="bg-emerald-500/60" pct={85} />
              </div>
              <div className="mt-6 pt-4 border-t border-white/10 flex items-center justify-between text-xs text-slate-400 font-mono-ui uppercase tracking-widest">
                <span>Circadian Score</span>
                <span className="text-indigo-300 text-base">82 / 100</span>
              </div>
            </div>
          </div>
        </div>

        {error && (
          <div className="mt-6 text-rose-400 text-sm font-mono-ui">{error}</div>
        )}
      </main>

      <ReviewsSection />

      <AuthModal open={authOpen} onOpenChange={setAuthOpen} defaultMode={authMode} />
    </div>
  );
}

function TimelinePreviewRow({ label, time, color, pct }) {
  return (
    <div>
      <div className="flex justify-between text-xs mb-1.5">
        <span className="text-slate-300 font-medium">{label}</span>
        <span className="text-slate-500 font-mono-ui">{time}</span>
      </div>
      <div className="h-2 rounded-full bg-slate-800/80 overflow-hidden">
        <div className={`h-full ${color}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}
